/**
 * useQuoteFields Hook - MA-ARCH-163
 * 
 * Extracted from ScriptEdit.jsx - Manages quote sheet fields state and CRUD.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { getQuoteFieldLabel, isCompanyQuoteField, isIssueAgeQuoteField, isProductQuoteField } from '@/lib/quoteFieldUtils';
import { QUOTE_LABEL_PRESETS, moveItem } from '../utils/scriptEditUtils';

/**
 * Hook for managing quote sheet fields
 * @param {string} scriptId - The script ID
 * @param {Function} autoSaveDraftIfDirty - Auto-save function from editor state
 * @param {boolean} canEdit - Whether user can edit
 */
export function useQuoteFields(scriptId, autoSaveDraftIfDirty, canEdit) {
  const [quoteFields, setQuoteFields] = useState([]);
  const [quoteTab, setQuoteTab] = useState("inputs");
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [quoteTemplateId, setQuoteTemplateId] = useState("");
  const [quoteTemplateLoading, setQuoteTemplateLoading] = useState(false);
  const [quoteTemplateApplying, setQuoteTemplateApplying] = useState(false);

  // Add field state
  const [quoteLabelMode, setQuoteLabelMode] = useState("preset");
  const [quoteNewPresetLabel, setQuoteNewPresetLabel] = useState(QUOTE_LABEL_PRESETS[0]);
  const [quoteNewCustomLabel, setQuoteNewCustomLabel] = useState("");
  const [quoteNewFieldType, setQuoteNewFieldType] = useState("text");
  const [quoteNewOptions, setQuoteNewOptions] = useState("");
  const [quoteNewIncludeInHousehold, setQuoteNewIncludeInHousehold] = useState(false);

  // Edit field state
  const [editQuoteOpen, setEditQuoteOpen] = useState(false);
  const [editQuoteField, setEditQuoteField] = useState(null);
  const [editQuoteLabel, setEditQuoteLabel] = useState("");
  const [editQuoteType, setEditQuoteType] = useState("text");
  const [editQuoteOptions, setEditQuoteOptions] = useState("");
  const [editQuoteIncludeInHousehold, setEditQuoteIncludeInHousehold] = useState(false);

  // Formula editor state
  const [formulaOpen, setFormulaOpen] = useState(false);
  const [formulaField, setFormulaField] = useState(null);
  const [formulaTokens, setFormulaTokens] = useState([]);
  const [formulaInsertValue, setFormulaInsertValue] = useState("");
  const [formulaNumberDraft, setFormulaNumberDraft] = useState("");

  // Derived: required quote field IDs
  const requiredQuoteFieldIds = useMemo(() => {
    const sorted = [...(quoteFields || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const ids = new Set();
    const companyField = sorted.find((f) => isCompanyQuoteField(getQuoteFieldLabel(f)));
    if (companyField?.id) ids.add(companyField.id);
    const productField = sorted.find((f) => isProductQuoteField(getQuoteFieldLabel(f)));
    if (productField?.id) ids.add(productField.id);
    const issueAgeField = sorted.find((f) => isIssueAgeQuoteField(getQuoteFieldLabel(f)));
    if (issueAgeField?.id) ids.add(issueAgeField.id);
    return ids;
  }, [quoteFields]);

  // Ensure required quote fields exist
  const ensureRequiredQuoteFields = useCallback(async (currentFields) => {
    if (!canEdit) return false;
    const list = Array.isArray(currentFields) ? currentFields : [];
    let changed = false;

    const removeDuplicates = async (fields) => {
      if (fields.length <= 1) return;
      const extras = fields.slice(1);
      for (const extra of extras) {
        await api.delete(`/quote_fields/${extra.id}`);
        changed = true;
      }
    };

    const ensureField = async (label, matcher, sortOrderOverride = null) => {
      const matches = list
        .filter((f) => matcher(getQuoteFieldLabel(f)))
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      await removeDuplicates(matches);
      const existing = matches[0];
      if (!existing) {
        await api.post(`/scripts/${scriptId}/quote_fields`, {
          label,
          kind: "input",
          field_type: "dropdown",
          sort_order: sortOrderOverride ?? list.length,
          options: [],
        });
        changed = true;
        return;
      }

      if (existing.kind !== "input" || existing.field_type !== "dropdown") {
        await api.put(`/quote_fields/${existing.id}`, {
          kind: "input",
          field_type: "dropdown",
          options: [],
        });
        changed = true;
      }
    };

    try {
      await ensureField("Company", isCompanyQuoteField);
      await ensureField("Product", isProductQuoteField);
      const productField = list.find((f) => isProductQuoteField(getQuoteFieldLabel(f)));
      const productSort = Number.isFinite(Number(productField?.sort_order)) ? Number(productField.sort_order) : null;
      const issueAgeSortOrder = productSort !== null ? productSort + 1 : list.length + 1;
      await ensureField("Issue Age", isIssueAgeQuoteField, issueAgeSortOrder);
    } catch {
      return false;
    }

    return changed;
  }, [canEdit, scriptId]);

  // Fetch quote fields
  const fetchQuoteFields = useCallback(async () => {
    try {
      const res = await api.get(`/scripts/${scriptId}/quote_fields`);
      const list = res.data || [];
      const changed = await ensureRequiredQuoteFields(list);
      if (changed) {
        const refreshed = await api.get(`/scripts/${scriptId}/quote_fields`);
        setQuoteFields(refreshed.data || []);
        return;
      }
      setQuoteFields(list);
    } catch {
      setQuoteFields([]);
    }
  }, [scriptId, ensureRequiredQuoteFields]);

  // Fetch quote templates
  const fetchQuoteTemplates = useCallback(async () => {
    setQuoteTemplateLoading(true);
    try {
      const res = await api.get("/quote-templates");
      setQuoteTemplates(Array.isArray(res.data) ? res.data : []);
    } catch {
      setQuoteTemplates([]);
    } finally {
      setQuoteTemplateLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchQuoteFields();
  }, [fetchQuoteFields]);

  useEffect(() => {
    if (!canEdit) return;
    fetchQuoteTemplates();
  }, [canEdit, fetchQuoteTemplates]);

  // Add quote field
  const onAddQuoteField = useCallback(async () => {
    try {
      await autoSaveDraftIfDirty?.();

      const label =
        quoteLabelMode === "custom"
          ? quoteNewCustomLabel.trim()
          : (quoteNewPresetLabel || "").trim();

      if (!label) return;

      if (isCompanyQuoteField(label) || isProductQuoteField(label) || isIssueAgeQuoteField(label)) {
        toast({ title: "Company, Product, and Issue Age are required fields", description: "They're already included on the Quote Sheet." });
        return;
      }

      const payload = {
        label,
        kind: quoteTab === "inputs" ? "input" : "calculated",
        field_type: quoteNewFieldType,
        sort_order: quoteFields.length,
      };

      if (payload.kind === "input") {
        payload.include_in_household = !!quoteNewIncludeInHousehold;
      }

      if (payload.kind === "input" && payload.field_type === "dropdown") {
        payload.options = quoteNewOptions
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }

      if (payload.kind === "calculated") {
        payload.formula = [];
      }

      await api.post(`/scripts/${scriptId}/quote_fields`, payload);
      toast({ title: "Quote field added" });

      // Reset form
      setQuoteLabelMode("preset");
      setQuoteNewPresetLabel(QUOTE_LABEL_PRESETS[0]);
      setQuoteNewCustomLabel("");
      setQuoteNewFieldType("text");
      setQuoteNewOptions("");
      setQuoteNewIncludeInHousehold(false);

      await fetchQuoteFields();
    } catch (e) {
      toast({
        title: "Add quote field failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [autoSaveDraftIfDirty, quoteLabelMode, quoteNewCustomLabel, quoteNewPresetLabel, quoteTab, quoteNewFieldType, quoteFields.length, quoteNewIncludeInHousehold, quoteNewOptions, scriptId, fetchQuoteFields]);

  // Delete quote field
  const onDeleteQuoteField = useCallback(async (fieldId) => {
    try {
      await autoSaveDraftIfDirty?.();
      await api.delete(`/quote_fields/${fieldId}`);
      toast({ title: "Quote field removed" });
      await fetchQuoteFields();
    } catch (e) {
      toast({
        title: "Delete quote field failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [autoSaveDraftIfDirty, fetchQuoteFields]);

  // Update quote field
  const onUpdateQuoteField = useCallback(async (fieldId, patch) => {
    try {
      await api.put(`/quote_fields/${fieldId}`, patch);
      await fetchQuoteFields();
    } catch (e) {
      toast({
        title: "Quote field update failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [fetchQuoteFields]);

  // Move quote field
  const onMoveQuoteField = useCallback(async (field, direction) => {
    const list = [...(quoteFields || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const idx = list.findIndex((f) => f.id === field.id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= list.length) return;
    const reordered = moveItem(list, idx, newIdx);
    const ids = reordered.map((f) => f.id);
    try {
      await api.put(`/scripts/${scriptId}/quote_fields/reorder`, { field_ids: ids });
      await fetchQuoteFields();
    } catch (e) {
      toast({
        title: "Move failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  }, [quoteFields, scriptId, fetchQuoteFields]);

  // Open edit input field dialog
  const openEditInputField = useCallback((field) => {
    setEditQuoteField(field);
    setEditQuoteLabel(field.label || "");
    setEditQuoteType(field.field_type || "text");
    setEditQuoteOptions(Array.isArray(field.options) ? field.options.join(", ") : "");
    setEditQuoteIncludeInHousehold(!!field.include_in_household);
    setEditQuoteOpen(true);
  }, []);

  // Open edit calculated field dialog
  const openEditCalculatedField = useCallback((field) => {
    setEditQuoteField(field);
    setEditQuoteLabel(field.label || "");
    setEditQuoteOpen(true);
  }, []);

  // Save edit
  const onSaveEditQuote = useCallback(async () => {
    if (!editQuoteField?.id) return;
    const isCalc = editQuoteField.kind === "calculated";

    const patch = { label: editQuoteLabel.trim() };
    if (!isCalc) {
      patch.field_type = editQuoteType;
      patch.include_in_household = !!editQuoteIncludeInHousehold;
      if (editQuoteType === "dropdown") {
        patch.options = editQuoteOptions
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }

    await onUpdateQuoteField(editQuoteField.id, patch);
    toast({ title: "Quote field updated" });
    setEditQuoteOpen(false);
    setEditQuoteField(null);
  }, [editQuoteField, editQuoteLabel, editQuoteType, editQuoteIncludeInHousehold, editQuoteOptions, onUpdateQuoteField]);

  // Formula editor functions
  const openFormulaEditor = useCallback((field) => {
    setFormulaField(field);
    setFormulaTokens(Array.isArray(field?.formula) ? field.formula : []);
    setFormulaInsertValue("");
    setFormulaNumberDraft("");
    setFormulaOpen(true);
  }, []);

  const addFormulaToken = useCallback((tok) => {
    setFormulaTokens((prev) => [...prev, tok]);
  }, []);

  const removeLastFormulaToken = useCallback(() => {
    setFormulaTokens((prev) => prev.slice(0, -1));
  }, []);

  const onSaveFormula = useCallback(async () => {
    if (!formulaField?.id) return;
    await onUpdateQuoteField(formulaField.id, { formula: formulaTokens });
    toast({ title: "Formula saved" });
    setFormulaOpen(false);
  }, [formulaField?.id, formulaTokens, onUpdateQuoteField]);

  // Apply quote template
  const onApplyQuoteTemplate = useCallback(async () => {
    if (!quoteTemplateId) return;
    setQuoteTemplateApplying(true);
    try {
      await api.post(`/scripts/${scriptId}/quote-templates/${quoteTemplateId}/apply`);
      toast({ title: "Quote template applied" });
      setQuoteTemplateId("");
      await fetchQuoteFields();
    } catch (e) {
      toast({
        title: "Apply template failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setQuoteTemplateApplying(false);
    }
  }, [quoteTemplateId, scriptId, fetchQuoteFields]);

  return {
    // State
    quoteFields,
    quoteTab,
    setQuoteTab,
    quoteTemplates,
    quoteTemplateId,
    setQuoteTemplateId,
    quoteTemplateLoading,
    quoteTemplateApplying,
    requiredQuoteFieldIds,

    // Add field state
    quoteLabelMode,
    setQuoteLabelMode,
    quoteNewPresetLabel,
    setQuoteNewPresetLabel,
    quoteNewCustomLabel,
    setQuoteNewCustomLabel,
    quoteNewFieldType,
    setQuoteNewFieldType,
    quoteNewOptions,
    setQuoteNewOptions,
    quoteNewIncludeInHousehold,
    setQuoteNewIncludeInHousehold,

    // Edit field state
    editQuoteOpen,
    setEditQuoteOpen,
    editQuoteField,
    editQuoteLabel,
    setEditQuoteLabel,
    editQuoteType,
    setEditQuoteType,
    editQuoteOptions,
    setEditQuoteOptions,
    editQuoteIncludeInHousehold,
    setEditQuoteIncludeInHousehold,

    // Formula state
    formulaOpen,
    setFormulaOpen,
    formulaField,
    formulaTokens,
    setFormulaTokens,
    formulaInsertValue,
    setFormulaInsertValue,
    formulaNumberDraft,
    setFormulaNumberDraft,

    // Actions
    fetchQuoteFields,
    fetchQuoteTemplates,
    onAddQuoteField,
    onDeleteQuoteField,
    onUpdateQuoteField,
    onMoveQuoteField,
    openEditInputField,
    openEditCalculatedField,
    onSaveEditQuote,
    openFormulaEditor,
    addFormulaToken,
    removeLastFormulaToken,
    onSaveFormula,
    onApplyQuoteTemplate,
  };
}

export default useQuoteFields;
